import { TrackedEvent, trackEvent } from "@/domains/metrics";
import {
  MDSListBox,
  MDSIconButton,
  MDSText,
  MDSTextWeight,
  MDSTextVariant,
  MDSIcon,
  MDSIconKind,
} from "@/mds";
import { MDSListBoxItemProps } from "@/mds/components/lists/list-box-item";
import { copyTextToClipboard } from "@/modules/clipboard/copyTextToClipboard";
import { toastModule } from "@/modules/toast";
import { uuidModule } from "@/modules/uuid";
import { IntegrationsApiPageContentForm } from "@/pages/integrations/api/integrations-api-page-content-form";
import {
  ApiKeyItemInfo,
  PendingApiKey,
} from "@/pages/integrations/api/integrations-api-page.types";
import { IntegrationsLayoutBottomBuffer } from "@/pages/integrations/shared/integrations-layout";
import { StyledHeading } from "@/pages/integrations/shared/integrations.styles";
import { AppStore, useAppStore } from "@/store";
import styled from "@emotion/styled";
import { observer } from "mobx-react-lite";
import { FC, useCallback, useEffect, useMemo, useState } from "react";

type Props = {};

export const ApiKeyItemList: FC<Props> = observer(() => {
  const { store } = useAppStore();

  const [pendingApiKey, setPendingApiKey] = useState<PendingApiKey | null>(null);
  const [formShown, setFormShown] = useState(false);
  const [nickname, setNickname] = useState("");

  const isLoading = store.apiKeys.isCreatingApiKey;

  const createApiKey = useCallback(async () => {
    const id = uuidModule.generate();
    const secretKeyToken = uuidModule.generate();

    await store.apiKeys.createApiKey({
      id,
      secretKeyToken,
      // Title and description are not set for now.
      title: null,
      description: null,
    });

    trackEvent(TrackedEvent.ApiKeyCreate, {
      api_key_id: id,
    });

    setPendingApiKey({ id, secretKeyToken });
  }, [store.apiKeys]);

  useEffect(() => {
    // When isCreatingApiKey becomes false and we have a pending key, show the modal
    if (!store.apiKeys.isCreatingApiKey && pendingApiKey) {
      setFormShown(true);
    }
  }, [store.apiKeys.isCreatingApiKey, pendingApiKey]);

  const onCreateHandler = useCallback(() => {
    if (isLoading) {
      return;
    }

    createApiKey();
  }, [createApiKey, isLoading]);

  const copyApiKey = useCallback(() => {
    if (pendingApiKey) {
      copyTextToClipboard({ text: `sk-mem-${pendingApiKey.secretKeyToken}` });
      toastModule.triggerToast({ content: "API key copied to clipboard" });
    }
  }, [pendingApiKey]);

  const deleteApiKey = useCallback(
    (info: ApiKeyItemInfo) => {
      store.apiKeys.openDeleteApiKeyModal({ id: info.id, title: info.title });
    },
    [store.apiKeys]
  );

  const handleCloseForm = () => {
    setFormShown(false);
    setPendingApiKey(null);
    setNickname("");
  };

  const { apiKeyInfoList, createItems } = useItems({
    store,
    formShown,
    isLoading,
    pendingApiKey,
    onCreateHandler,
  });
  const listHasItems = apiKeyInfoList.length > 0 || createItems.length > 0;

  return (
    <Wrapper listHasItems={listHasItems}>
      <StyledHeading variant={MDSTextVariant.TextXs} weight={MDSTextWeight.SemiBold}>
        Activated API keys
      </StyledHeading>
      {apiKeyInfoList.length > 0 && (
        <ApiKeyRows>
          {apiKeyInfoList.map(info => (
            <ApiKeyRow key={info.id}>
              <TitleText>
                <MDSText variant={MDSTextVariant.TextS}>{info.title}</MDSText>
              </TitleText>
              <SecretTokenText>
                <MDSText variant={MDSTextVariant.TextS}>{info.redactedToken}</MDSText>
              </SecretTokenText>
              <MDSIconButton
                iconKind={MDSIconKind.Trash}
                aria-label="Delete API key"
                onPress={() => deleteApiKey(info)}
              />
            </ApiKeyRow>
          ))}
        </ApiKeyRows>
      )}
      {createItems.length > 0 && <MDSListBox items={createItems} />}

      {formShown && (
        <IntegrationsApiPageContentForm
          nickname={nickname}
          pendingApiKey={pendingApiKey}
          copyApiKey={copyApiKey}
          handleCloseForm={handleCloseForm}
          setNickname={setNickname}
        />
      )}
      <IntegrationsLayoutBottomBuffer />
    </Wrapper>
  );
});

const useItems = ({
  store,
  formShown,
  isLoading,
  pendingApiKey,
  onCreateHandler,
}: {
  store: AppStore;
  formShown: boolean;
  isLoading: boolean;
  pendingApiKey: PendingApiKey | null;
  onCreateHandler: () => void;
}) => {
  const apiKeyInfoList: ApiKeyItemInfo[] = useMemo(() => {
    return store.apiKeys.allActiveApiKeys
      .filter(apiKey => apiKey.id !== pendingApiKey?.id)
      .map(apiKey => {
        return {
          id: apiKey.id,
          title: apiKey.title,
          createdAt: apiKey.createdAt,
          redactedToken: apiKey.redactedToken,
        };
      })
      .sort((a, b) => a.createdAt.toMillis() - b.createdAt.toMillis());
  }, [store.apiKeys.allActiveApiKeys, pendingApiKey]);

  const createItems: MDSListBoxItemProps[] = [];

  if (!formShown) {
    createItems.push({
      id: "create-api-key",
      textValue: "Create API key",
      onAction: onCreateHandler,
      isDisabled: isLoading,
      leftAddon: (
        <IconWrapper>
          <MDSIcon kind={MDSIconKind.Plus} size={16} />
        </IconWrapper>
      ),
      children: (
        <MDSText variant={MDSTextVariant.TextS}>
          {isLoading ? "Creating API key..." : "Add new API key"}
        </MDSText>
      ),
    });
  }

  return { apiKeyInfoList, createItems };
};

const Wrapper = styled.div<{ listHasItems: boolean }>(({ theme, listHasItems }) => ({
  width: "100%",
  marginTop: theme.spacing["2"],
  padding: 0,
  display: "flex",
  height: "100%",
  flexDirection: "column",
  gap: listHasItems ? theme.spacing["2"] : theme.spacing["1"],
  scrollbarWidth: "none",
  "&::-webkit-scrollbar": {
    display: "none",
  },
}));

const TitleText = styled.div(({ theme }) => ({
  width: "120px",
  height: 36,
  [theme.mediaQueries.rules.phone]: {
    width: "50px",
  },
  overflow: "hidden",
  whiteSpace: "nowrap",
  textOverflow: "ellipsis",
  display: "flex",
  alignItems: "center",
  paddingLeft: theme.spacing[2],
}));

const ApiKeyRows = styled.div(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing["3"],
  width: "100%",
}));

const ApiKeyRow = styled.div(({ theme }) => ({
  display: "flex",
  maxWidth: 784,
  padding: `${theme.spacing[4]} ${theme.spacing[3]}`,
  justifyContent: "space-between",
  alignItems: "center",
  borderRadius: theme.borderRadius.medium,
  border: `1px solid ${theme.colors.border.default}`,
  background: theme.colors.surface.pane,
  gap: theme.spacing[3],
}));

const SecretTokenText = styled.div(() => ({
  fontFamily: "monaco, monospace",
  display: "flex",
  flex: 1,
  minWidth: 0,
}));

const IconWrapper = styled.div<{ variant?: "primary" | "secondary" }>(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: theme.spacing[2],

  height: 32,
  width: 32,
}));
